import { useState, useRef, useCallback, useEffect } from 'react'
import styles from './ImportSection.module.css'

export default function ImportSection({ apiToken, user }) {
    const [dragActive, setDragActive] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [importStatus, setImportStatus] = useState(null)
    const [importHistory, setImportHistory] = useState([])
    const [selectedFile, setSelectedFile] = useState(null)
    const [errors, setErrors] = useState([])
    const [currentImportId, setCurrentImportId] = useState(null)
    const fileInputRef = useRef(null)
    const pollIntervalRef = useRef(null)
    const progressContainerRef = useRef(null)

    // Check if user is admin  
    const isAdmin = user?.workspace_role === 'admin' || true  // Temporarily allow all users for testing

    useEffect(() => {
        if (isAdmin) {
            fetchImportHistory()
        }
    }, [isAdmin])

    useEffect(() => {
        if (currentImportId && importStatus?.status === 'processing') {
            startPolling()
        } else {
            stopPolling()
        }
        
        return () => stopPolling()
    }, [currentImportId, importStatus?.status])

    const startPolling = () => {
        stopPolling()
        pollIntervalRef.current = setInterval(() => {
            fetchImportStatus(currentImportId, false) // polling, not manual view
        }, 2000)
    }

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
    }

    const fetchImportHistory = async () => {
        try {
            const response = await fetch('/api/v1/imports', {
                headers: {
                    'X-Auth-Token': apiToken,
                    'Content-Type': 'application/json'
                }
            })
            
            if (response.ok) {
                const data = await response.json()
                setImportHistory(data.imports || [])
            }
        } catch (error) {
            console.error('Failed to fetch import history:', error)
        }
    }

    const fetchImportStatus = async (importId, isManualView = false) => {
        try {
            const response = await fetch(`/api/v1/imports/${importId}`, {
                headers: {
                    'X-Auth-Token': apiToken,
                    'Content-Type': 'application/json'
                }
            })
            
            if (response.ok) {
                const data = await response.json()
                setImportStatus(data)
                
                // Only clear currentImportId if we're polling (not manually viewing)
                if (!isManualView && (data.status === 'completed' || data.status === 'failed')) {
                    setCurrentImportId(null)
                    fetchImportHistory() // Refresh history
                    
                    if (data.status === 'failed' && data.errors?.length > 0) {
                        setErrors(data.errors)
                    }
                }
                
                // If manually viewing and it has errors, fetch them
                if (isManualView && data.status === 'failed' && data.errors?.length > 0) {
                    setErrors(data.errors)
                }
            }
        } catch (error) {
            console.error('Failed to fetch import status:', error)
        }
    }

    const scrollToImportDetails = () => {
        setTimeout(() => {
            if (progressContainerRef.current) {
                progressContainerRef.current.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                })
            }
        }, 100) // Small delay to ensure the component has rendered
    }

    const fetchImportErrors = async (importId) => {
        try {
            const response = await fetch(`/api/v1/imports/${importId}/errors`, {
                headers: {
                    'X-Auth-Token': apiToken,
                    'Content-Type': 'application/json'
                }
            })
            
            if (response.ok) {
                const data = await response.json()
                setErrors(data.errors || [])
            }
        } catch (error) {
            console.error('Failed to fetch import errors:', error)
        }
    }

    const handleDrag = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true)
        } else if (e.type === 'dragleave') {
            setDragActive(false)
        }
    }, [])

    const handleDrop = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0])
        }
    }, [])

    const handleFileSelect = (file) => {
        if (!file) return
        
        // Validate file type
        if (!file.name.toLowerCase().endsWith('.csv')) {
            alert('Please select a CSV file')
            return
        }
        
        // Validate file size (50MB max)
        if (file.size > 50 * 1024 * 1024) {
            alert('File size must be less than 50MB')
            return
        }
        
        setSelectedFile(file)
        setErrors([])
        setImportStatus(null)
    }

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0])
        }
    }

    const uploadFile = async () => {
        if (!selectedFile) return
        
        setUploading(true)
        setImportStatus(null)
        setErrors([])
        
        const formData = new FormData()
        formData.append('file', selectedFile)
        
        try {
            const response = await fetch('/api/v1/imports', {
                method: 'POST',
                headers: {
                    'X-Auth-Token': apiToken
                },
                body: formData
            })
            
            const data = await response.json()
            
            if (response.ok) {
                setCurrentImportId(data.import_id)
                setImportStatus({
                    id: data.import_id,
                    status: 'processing',
                    total_records: data.total_records,
                    processed_records: 0,
                    progress_percentage: 0
                })
                setSelectedFile(null)
                if (fileInputRef.current) {
                    fileInputRef.current.value = ''
                }
            } else {
                setErrors([{ message: data.error || 'Upload failed' }])
            }
        } catch (error) {
            setErrors([{ message: 'Network error occurred' }])
        } finally {
            setUploading(false)
        }
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return styles.statusCompleted
            case 'failed': return styles.statusFailed
            case 'processing': return styles.statusProcessing
            default: return styles.statusPending
        }
    }

    if (!isAdmin) {
        return (
            <div className={styles.importSection}>
                <div className={styles.accessDenied}>
                    <h2>Access Denied</h2>
                    <p>Only workspace administrators can access the import functionality.</p>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.importSection}>
            <div className={styles.header}>
                <h2>Import Worklog Data</h2>
                <p>Upload a CSV file to import time tracking data into your workspace. All missing users, projects, clients, and tasks will be created automatically.</p>
            </div>

            {/* Upload Area */}
            <div className={styles.uploadContainer}>
                <div 
                    className={`${styles.dropZone} ${dragActive ? styles.dragActive : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                        disabled={uploading}
                    />
                    
                    <div className={styles.dropContent}>
                        <div className={styles.uploadIcon}>📁</div>
                        {selectedFile ? (
                            <div>
                                <p><strong>Selected:</strong> {selectedFile.name}</p>
                                <p className={styles.fileSize}>
                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        ) : (
                            <div>
                                <p>Drag and drop your CSV file here, or click to browse</p>
                                <p className={styles.requirements}>
                                    Supported format: CSV files up to 50MB
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {selectedFile && (
                    <div className={styles.uploadActions}>
                        <button 
                            className={styles.uploadBtn}
                            onClick={uploadFile}
                            disabled={uploading}
                        >
                            {uploading ? 'Uploading...' : 'Start Import'}
                        </button>
                        <button 
                            className={styles.cancelBtn}
                            onClick={() => {
                                setSelectedFile(null)
                                if (fileInputRef.current) fileInputRef.current.value = ''
                            }}
                            disabled={uploading}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            {/* Progress Section */}
            {importStatus && (
                <div ref={progressContainerRef} className={styles.progressContainer}>
                    <h3>Import Details</h3>
                    <div className={styles.progressInfo}>
                        <div className={styles.progressStats}>
                            <span>Status: <strong className={getStatusColor(importStatus.status)}>{importStatus.status.toUpperCase()}</strong></span>
                            <span>Progress: {importStatus.processed_records || 0} / {importStatus.total_records || 0} records</span>
                            <span>Success: {importStatus.success_records || 0}</span>
                            <span>Failed: {importStatus.failed_records || 0}</span>
                        </div>

                        {/* Debug Statistics */}
                        
                        {/* Debug display for checking data structure */}
                        <div style={{background: '#f0f0f0', padding: '10px', margin: '10px 0', fontSize: '0.8rem', fontFamily: 'monospace'}}>
                            <strong>Debug Info:</strong><br/>
                            ID: {importStatus.id}<br/>
                            Status: {importStatus.status}<br/>
                            Total Records: {importStatus.total_records}<br/>
                            Has Statistics: {importStatus.statistics ? 'Yes' : 'No'}<br/>
                            Statistics Keys: {importStatus.statistics ? Object.keys(importStatus.statistics).join(', ') : 'None'}
                        </div>
                        
                        {/* Detailed Statistics */}
                        {(importStatus.statistics && Object.keys(importStatus.statistics).length > 0) || importStatus.status === 'completed' ? (
                            <div className={styles.detailedStats}>
                                <h4>Import Details - Created vs Existing</h4>
                                <div className={styles.statsGrid}>
                                    <div className={styles.statCategory}>
                                        <h5>👥 Users</h5>
                                        <span>✅ Created: {importStatus.statistics.users_created || 0}</span>
                                        <span>🔄 Existing: {importStatus.statistics.users_reused || 0}</span>
                                    </div>
                                    <div className={styles.statCategory}>
                                        <h5>🏢 Clients</h5>
                                        <span>✅ Created: {importStatus.statistics.clients_created || 0}</span>
                                        <span>🔄 Existing: {importStatus.statistics.clients_reused || 0}</span>
                                    </div>
                                    <div className={styles.statCategory}>
                                        <h5>📁 Projects</h5>
                                        <span>✅ Created: {importStatus.statistics.projects_created || 0}</span>
                                        <span>🔄 Existing: {importStatus.statistics.projects_reused || 0}</span>
                                    </div>
                                    <div className={styles.statCategory}>
                                        <h5>📝 Tasks</h5>
                                        <span>✅ Created: {importStatus.statistics.tasks_created || 0}</span>
                                        <span>🔄 Existing: {importStatus.statistics.tasks_reused || 0}</span>
                                    </div>
                                    <div className={styles.statCategory}>
                                        <h5>👨‍👩‍👧‍👦 Groups</h5>
                                        <span>✅ Created: {importStatus.statistics.groups_created || 0}</span>
                                        <span>🔄 Existing: {importStatus.statistics.groups_reused || 0}</span>
                                    </div>
                                    <div className={styles.statCategory}>
                                        <h5>⏱️ Worklogs</h5>
                                        <span>✅ Created: {importStatus.statistics.worklogs_created || 0}</span>
                                        <span>🔄 Updated: {importStatus.statistics.worklogs_updated || 0}</span>
                                        <span>⚠️ Duplicates Ignored: {importStatus.statistics.worklogs_reused || 0}</span>
                                    </div>
                                </div>
                            </div>
                        ) : importStatus.status === 'completed' && (
                            <div className={styles.detailedStats}>
                                <h4>Import Completed</h4>
                                <p>This import was processed before detailed statistics were available. Re-import the same file to see detailed created vs. existing breakdowns.</p>
                            </div>
                        )}
                    </div>
                    
                    <div className={styles.progressBar}>
                        <div 
                            className={styles.progressFill}
                            style={{ width: `${importStatus.progress_percentage || 0}%` }}
                        />
                    </div>
                    <div className={styles.progressText}>
                        {importStatus.progress_percentage?.toFixed(1) || 0}% Complete
                    </div>

                    {importStatus.error_message && (
                        <div className={styles.errorMessage}>
                            <strong>Error:</strong> {importStatus.error_message}
                        </div>
                    )}
                </div>
            )}

            {/* Error Display */}
            {errors.length > 0 && (
                <div className={styles.errorsContainer}>
                    <h3>Import Errors</h3>
                    <div className={styles.errorsList}>
                        {errors.slice(0, 10).map((error, index) => (
                            <div key={index} className={styles.errorItem}>
                                <strong>Row {error.row}:</strong> {error.field} - {error.message}
                            </div>
                        ))}
                        {errors.length > 10 && (
                            <div className={styles.moreErrors}>
                                ... and {errors.length - 10} more errors
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* CSV Format Requirements */}
            <div className={styles.formatRequirements}>
                <h3>CSV Format Requirements</h3>
                <div className={styles.requirements}>
                    <div className={styles.requiredColumns}>
                        <h4>Required Columns:</h4>
                        <ul>
                            <li>Project</li>
                            <li>Client</li>
                            <li>User</li>
                            <li>Email</li>
                            <li>Start Date (YYYY-MM-DD)</li>
                            <li>Start Time (HH:MM)</li>
                            <li>End Date (YYYY-MM-DD)</li>
                            <li>End Time (HH:MM)</li>
                            <li>Duration (decimal) - hours in decimal format</li>
                            <li>Billable (Yes/No)</li>
                        </ul>
                    </div>
                    <div className={styles.optionalColumns}>
                        <h4>Optional Columns:</h4>
                        <ul>
                            <li>Description</li>
                            <li>Task</li>
                            <li>Group</li>
                            <li>Tags</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Import History */}
            {importHistory.length > 0 && (
                <div className={styles.historyContainer}>
                    <h3>Recent Imports</h3>
                    <div className={styles.historyList}>
                        {importHistory.map((importRecord) => (
                            <div 
                                key={importRecord.id} 
                                className={`${styles.historyItem} ${styles.clickableItem}`}
                                onClick={() => {
                                    setCurrentImportId(importRecord.id)
                                    fetchImportStatus(importRecord.id, true)
                                    scrollToImportDetails()
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className={styles.historyInfo}>
                                    <div className={styles.historyHeader}>
                                        <strong>{importRecord.filename}</strong>
                                        <span className={getStatusColor(importRecord.status)}>
                                            {importRecord.status.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className={styles.historyDetails}>
                                        <span>By: {importRecord.user_name}</span>
                                        <span>Total: {importRecord.total_records} records</span>
                                        <span>Success: {importRecord.success_records}</span>
                                        <span>Failed: {importRecord.failed_records}</span>
                                        <span>Progress: {importRecord.progress_percentage}%</span>
                                        <span>{formatDate(importRecord.created_at)}</span>
                                    </div>
                                </div>
                                <div className={styles.itemActions}>
                                    <button 
                                        className={styles.viewDetailsBtn}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setCurrentImportId(importRecord.id)
                                            fetchImportStatus(importRecord.id, true)
                                            scrollToImportDetails()
                                        }}
                                    >
                                        View Details
                                    </button>
                                    {importRecord.failed_records > 0 && (
                                        <button 
                                            className={styles.viewErrorsBtn}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                fetchImportErrors(importRecord.id)
                                            }}
                                        >
                                            View Errors
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}